import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import AlertSettings from "./AlertSettings";
import CalendarFeedSettings from "./CalendarFeedSettings";
import WatchlistShareSettings from "./WatchlistShareSettings";
import { apiClient } from "../lib/apiClient";
import { UserPlan } from "../lib/planCapabilities";
import { useDisplayPreferences } from "../hooks/useDisplayPreferences";
import { useFocusTrap } from "../hooks/useFocusTrap";

interface SettingsPanelProps {
  onClose: () => void;
  isPro: boolean;
  /** Needed to decide whether "Change county" shows at all — only
   *  free/scout participate in the geography-aware tier redesign (#864);
   *  pro/team/admin have unrestricted access everywhere and there's
   *  nothing for them to pick. */
  userPlan: UserPlan;
  /** Opens the existing CountyPickerModal (owned by Dashboard, which
   *  already has chosenCounties/suggestedCounty/the save-and-error-handling
   *  wiring) — this panel doesn't duplicate any of that, just triggers it. */
  onChangeCounty: () => void;
  /** Fired when the billing portal request comes back "no subscription
   *  found" (a free user with no Stripe customer yet — not an error, just
   *  nothing to manage). Dashboard turns this into the on-brand
   *  UpgradePrompt instead of this panel showing a raw browser alert(). */
  onNoSubscription: () => void;
  /** Fired after a display preference is SUCCESSFULLY saved (#1465), so
   *  Dashboard can refetch the property list — the resolved-row filter is
   *  applied server-side, so the currently-loaded rows are stale the moment
   *  the toggle flips. Not fired on a failed save, which reverts. */
  onDisplayPreferenceChange?: () => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  onClose,
  isPro,
  userPlan,
  onChangeCounty,
  onNoSubscription,
  onDisplayPreferenceChange,
}) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const {
    showResolved,
    loading: displayLoading,
    saving: displaySaving,
    error: displayError,
    setShowResolved,
  } = useDisplayPreferences();
  const [portalLoading, setPortalLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await apiClient.post<{ url: string }>("/billing/portal");
      window.location.href = data.url;
    } catch (err: any) {
      if (err?.response?.data?.error === "no subscription found") {
        setPortalLoading(false);
        onNoSubscription();
        return;
      }
      alert(err?.response?.data?.error || "Could not open billing portal");
      setPortalLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await apiClient.delete("/profile");
      await signOut();
      onClose();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Failed to delete account");
      setDeleting(false);
    }
  };

  return (
    <div className="settings-backdrop" onClick={onClose}>
      <div
        className="settings-panel"
        onClick={(e) => e.stopPropagation()}
        ref={useFocusTrap<HTMLDivElement>({ onEscape: onClose })}
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-panel-title"
      >
        <div className="settings-header">
          <h2 id="settings-panel-title">Settings</h2>
          <button className="auth-close" onClick={onClose} aria-label="Close settings">×</button>
        </div>
        <div className="settings-body">
          <section className="settings-section">
            <h3>Account</h3>
            <div className="settings-account">
              <div>
                <label>Email</label>
                <p>{user?.email}</p>
              </div>
            </div>
          </section>

          <section className="settings-section">
            <h3>Subscription</h3>
            <button className="settings-btn" onClick={openBillingPortal} disabled={portalLoading}>
              {portalLoading ? "Opening…" : "Manage Subscription"}
            </button>
            {/* /redeem (#2258) had no in-app entry point — only links handed
                out with a code. Shown for every plan: a paid user may hold a
                code for a later period. /redeem is its own route, so
                navigating unmounts the Dashboard and this panel with it. */}
            <p className="settings-section-note settings-redeem">
              Have a promo code?{" "}
              <button
                type="button"
                className="settings-link-btn"
                onClick={() => navigate("/redeem")}
              >
                Redeem it
              </button>
            </p>
          </section>

          {(userPlan === "free" || userPlan === "scout") && (
            <section className="settings-section">
              <h3>County</h3>
              <p className="settings-section-note">
                Full property data (assessed value, equity) shows only in your chosen{" "}
                {userPlan === "free" ? "county" : "counties"}. You can browse every county
                on the map either way.
              </p>
              <button className="settings-btn" onClick={onChangeCounty}>
                Change county
              </button>
            </section>
          )}

          <section className="settings-section">
            <AlertSettings isPro={isPro} />
          </section>

          <section className="settings-section">
            <CalendarFeedSettings />
          </section>

          <section className="settings-section">
            <WatchlistShareSettings />
          </section>

          {/* Free viewers are hard-gated server-side to active statuses and
              never see resolved properties at all, so there is nothing here
              for them to toggle (#1465). Showing a control that does nothing
              would be worse than showing none. Scout is a paid full-row-set
              plan: GetProperties honours profiles.show_resolved for it exactly
              as for Pro, so it gets the toggle. */}
          {userPlan !== "free" && (
            <section className="settings-section">
              <h3>Map &amp; list</h3>
              <p className="settings-section-note">
                Resolved properties — sold, redeemed, withdrawn, passed, or delisted —
                are hidden from the map and list by default so you can scan for live
                deals. They stay searchable either way: look one up by address, owner,
                or case number and it will still come back.
              </p>
              {/* Reuses AlertSettings' .alert-row / .alert-switch markup
                  verbatim rather than introducing a second toggle style —
                  it's the app's established switch and it already lives in
                  this same panel a few sections down. No new CSS. */}
              <div className="alert-row">
                <div className="alert-info">
                  <div className="alert-title">Show resolved properties</div>
                  <div className="alert-desc">
                    {displayError
                      ? displayError
                      : "Off by default. Turning this on adds sold, redeemed, withdrawn, passed and delisted properties back to the map and list."}
                  </div>
                </div>
                <label className="alert-switch">
                  <input
                    type="checkbox"
                    checked={showResolved}
                    disabled={displayLoading || displaySaving}
                    onChange={async (e) => {
                      const ok = await setShowResolved(e.target.checked);
                      if (ok) onDisplayPreferenceChange?.();
                    }}
                  />
                  <span className="alert-slider" />
                </label>
              </div>
            </section>
          )}

          <section className="settings-section">
            <h3>Legal</h3>
            <div className="settings-legal-links">
              <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
              <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            </div>
          </section>

          <section className="settings-section settings-danger">
            <h3>Danger Zone</h3>
            {!confirmDelete ? (
              <button
                className="settings-btn settings-btn--danger"
                onClick={() => setConfirmDelete(true)}
              >
                Delete Account
              </button>
            ) : (
              <div className="settings-danger-confirm">
                <p>
                  This will permanently delete your account, watchlist, notes, and
                  alert preferences. This cannot be undone.
                </p>
                <div className="settings-danger-actions">
                  <button
                    className="settings-btn"
                    onClick={() => setConfirmDelete(false)}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    className="settings-btn settings-btn--danger"
                    onClick={handleDeleteAccount}
                    disabled={deleting}
                  >
                    {deleting ? "Deleting…" : "Confirm Delete"}
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
